import { toggleLoader, updateURL, addNavigationButtons, transitionPage, setGalleryView, scrollToGalleryTop } from './gallery-functions.js';
import { initGallerySidebar } from './gallery-sidebar.js';

let currentProcedureId = null;
let currentProcedureName = '';

export function initGallerySingle(patientId, procedureId, procedureName) {
    setGalleryView('single');

    currentProcedureId = procedureId;
    currentProcedureName = procedureName;
    
    // Show the sidebar
    const sidebar = document.querySelector('.gallery-sidebar');
    if (sidebar) {
        sidebar.style.display = 'block';
    }

    document.getElementById('gallery-main').style.display = 'none';
    initGallerySidebar(procedureId);
    loadSinglePatient(patientId);
}

function loadSinglePatient(patientId) {
    toggleLoader(true);
    scrollToGalleryTop();
    
    let xhr = new XMLHttpRequest();
    xhr.open('POST', ajaxurl, true);
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    xhr.onreadystatechange = function() {
        if (xhr.readyState === 4) {
            toggleLoader(false);
            if (xhr.status === 200) {
                const response = JSON.parse(xhr.responseText);
                if (response.success) {
                    renderSinglePatient(response.data);
                    updateURL({patientId: patientId, procedureId: currentProcedureId});
                    addNavigationButtons(
                        `Back to ${currentProcedureName}`, 
                        response.data.procedure_page_url,
                        () => goBackToProcedure()
                    );

                    if (window.initMobileSidebar) {
                        window.initMobileSidebar();
                    }

                } else {
                    console.error('Server returned error:', response.data);
                    document.getElementById('patients-container').innerHTML = 
                        '<p>Failed to load patient data: ' + response.data + '</p>';
                    addNavigationButtons(
                        `Back to ${currentProcedureName}`, 
                        null,
                        () => goBackToProcedure()
                    );
                }
            } else {
                console.error('XHR failed. Status:', xhr.status, 'Response:', xhr.responseText);
                document.getElementById('patients-container').innerHTML = 
                    '<p>Failed to load patient data. Status: ' + xhr.status + '</p>';
                addNavigationButtons(
                    `Back to ${currentProcedureName}`, 
                    null,
                    () => goBackToProcedure()
                );
            }
        }
    };

    let data = 'action=get_single_patient&patient_id=' + patientId + '&procedure_id=' + currentProcedureId;
    xhr.send(data);
}

function renderPatientDetails(patient) {
    let detailsHtml = '';

    // Basic patient information
    const basicInfo = [
        { field: 'age', label: 'Age' },
        { field: 'gender', label: 'Gender' },
        { field: 'ethnicity', label: 'Ethnicity' },
        { field: 'weight', label: 'Weight' },
        { field: 'height', label: 'Height' }
    ];

    // Description section (WYSIWYG content)
    if (patient.description) {
        detailsHtml += `
            <div class="patient-description">
                <span>Description:</span>
                ${patient.description}
            </div>`;
    }

    // Create list for basic information if any exists
    const hasBasicInfo = basicInfo.some(info => patient[info.field]);
    if (hasBasicInfo) {
        detailsHtml += '<ul class="patient-details-list">';
        basicInfo.forEach(info => {
            if (patient[info.field]) {
                detailsHtml += `
                    <li class="patient-detail-item">
                        <span class="detail-label">${info.label}:</span>
                        <span class="detail-value">${patient[info.field]}</span>
                    </li>`;
            }
        });
        detailsHtml += '</ul>';
    }

    // Custom fields from repeater
    if (patient.custom && patient.custom.length > 0) {
        detailsHtml += '<ul class="patient-custom-details-list">';
        patient.custom.forEach(item => {
            if (item.label && item.value) {
                detailsHtml += `
                    <li class="patient-detail-item">
                        <span class="detail-label">${item.label}:</span>
                        <span class="detail-value">${item.value}</span>
                    </li>`;
            }
        });
        detailsHtml += '</ul>';
    }

    return detailsHtml;
}

function renderSinglePatient(patient) {
    transitionPage(() => {
        const patientsContainer = document.getElementById('patients-container');
        let patientHtml = 
            '<div class="single-patient">' +
                '<div class="single-patient-header">' +
                    '<h2>' + currentProcedureName + ' Case #' + patient.title + '</h2>' +
                    renderPatientNavigation(patient.prev_patient, patient.next_patient, 'patient-navigation--header') +
                '</div>';

        // Photos section
        if (patient.photos && patient.photos.length > 0) {
            // Add the conditional class based on case_type
            const photosClass = `patient-photos patient-photos--${patient.case_type || 'double'}`;
            patientHtml += `<div class="${photosClass}">`;
            patient.photos.forEach(function(photo) {
                patientHtml += 
                    '<div class="patient-photo">' +
                        '<img src="' + photo.url + '" alt="' + photo.alt + '">' +
                        (!patient.disable_labels && photo.label ? '<p class="patient-photo-label">' + photo.label + '</p>' : '') +
                    '</div>';
            });
            patientHtml += '</div>';
        }

        // Patient information section
        patientHtml += '<div class="patient-information">';
            patientHtml += renderPatientDetails(patient);
        patientHtml += '</div>';

        patientHtml += '</div>';

        // Navigation section
        // Bottom navigation
        patientHtml += renderPatientNavigation(patient.prev_patient, patient.next_patient, 'patient-navigation--footer');

        patientHtml += '</div>';

        patientsContainer.innerHTML = patientHtml;

        // Add event listeners to all navigation buttons
        document.querySelectorAll('.prev-patient-btn, .next-patient-btn').forEach(btn => {
            btn.addEventListener('click', () => loadSinglePatient(btn.dataset.id));
        });
    });
}

// Create a new reusable function for patient navigation
function renderPatientNavigation(prevPatient, nextPatient, classes = '') {
    let navigationHtml = '';
    if (prevPatient || nextPatient) {
        navigationHtml = `
            <div class="patient-navigation ${classes}">
                ${prevPatient ? 
                    `<button class="btn btn--outline prev-patient-btn" data-id="${prevPatient}">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                        <span>Previous</span>
                    </button>` : 
                    '<div></div>'
                }
                ${nextPatient ? 
                    `<button class="btn btn--outline next-patient-btn" data-id="${nextPatient}">
                        <span>Next</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </button>` : 
                    '<div></div>'
                }
            </div>`;
    }
    return navigationHtml;
}

function goBackToProcedure() {
    if (window.initGalleryListing) {
        window.initGalleryListing(currentProcedureId, false, currentProcedureName);
    } else {
        console.error('initGalleryListing function not found');
        history.back();
    }
}